import { useStudioAdminStudioStore } from '@apps/studioadmin/stores/studioAdminStudio.store';
import type { identifier } from '@core/models/base';
import { useSimpleRestService } from '@shared/composables/useSimpleRestService';
import type {
  OrganizationCreateModel,
  OrganizationDisplayModel,
  OrganizationUpdateModel,
} from '@shared/models/organizations/organizations';
import { get } from '@shared/axios';
import { useStudioOrganizationContactService } from '@apps/studioadmin/services/studioAdminOrganizationContacts.service';
import type { ServiceResult } from '@core/models/actionResponse';
import { isServiceSuccess } from '@core/models/actionResponse';
import type { ContactUpsertModel } from '@shared/models/customer/contact';

export const useOrganizationService = (studioId: identifier) => {
  const urlBase = `/api/studioadmin/studios/${studioId}/organizations`;
  const {
    fetchSingle: getOrganization,
    update: updateOrganization,
    create: createOrganization,
    createOrUpdate: createOrUpdateOrganization,
  } = useSimpleRestService<OrganizationDisplayModel, OrganizationCreateModel, OrganizationUpdateModel>(urlBase);

  const validateName = async (name: string) =>
    await get<boolean>(`${urlBase}/valid`, {
      name,
    });

  interface OrganizationBuildOptions {
    data: OrganizationCreateModel | OrganizationUpdateModel;
    id?: number;
  }
  interface ContactBuildOptions {
    data: ContactUpsertModel;
    id?: number;
  }

  interface BuildOptions {
    organization: OrganizationBuildOptions;
    contact: ContactBuildOptions;
  }

  /**
   * Creates or updates an organization and its contact (address)
   * @param options data and id for organization and contact
   */
  const buildOrganization = async (options: BuildOptions) => {
    const organizationResult = await createOrUpdateOrganization(options.organization.id, options.organization.data);

    let contactResult: ServiceResult<ContactUpsertModel> | undefined = undefined;
    if (isServiceSuccess(organizationResult)) {
      const { upsertStudioOrganizationContact } = useStudioOrganizationContactService(
        studioId,
        organizationResult.data.id,
      );

      contactResult = await upsertStudioOrganizationContact(options.contact.id, options.contact.data);
    }

    return {
      organizationResult,
      contactResult,
    };
  };

  return {
    buildOrganization,
    createOrganization,
    updateOrganization,
    createOrUpdateOrganization,
    getOrganization,
    validateName,
  };
};

export const useOrganizationServiceFromStore = () => {
  const { studioId } = useStudioAdminStudioStore();
  return useOrganizationService(studioId);
};
